import type { Component } from 'solid-js'
import { createResource, createSignal, Show, createMemo } from 'solid-js'
import {
  getDateRangesForSimplePeriod,
  getDateRangesForCustomPeriod,
  formatDateToApiString,
  type SimplePeriodType,
  type CompareMode
} from '../../shared/helpers/date.functions'
import { useCorisEngagement } from '../../shared/providers/coris/coris-engagement.provider'
import { useUser } from '../../shared/providers/user-provider'
import { usePeriods } from '../../shared/providers/period.provider'
import PeriodSelector from '../shared/components/PeriodSelector'
import SummaryCard from '../shared/components/SummaryCard'
import ChatChart from '../shared/components/ChatChart'
import ChatRoomsTable from '../shared/components/ChatRoomsTable'
import EmptyState from '../shared/components/EmptyState'
import { sticky } from '../../shared/directives/sticky'
import { SkeletonSummaryCard, SkeletonChart } from '../shared/components/Skeleton'

// Prevent tree-shaking of directive
sticky

const PAGE_SIZE = 15

const Chat: Component = () => {
  const { engagementPeriod, setEngagementPeriod } = usePeriods()
  const [page, setPage] = createSignal(1)
  const [isStuck, setIsStuck] = createSignal(false)

  const { getChat } = useCorisEngagement()
  const { organizationSlug } = useUser()

  const [sortBy, setSortBy] = createSignal<string>('messages')
  const [sortOrder, setSortOrder] = createSignal<'asc' | 'desc'>('desc')

  const [chatData] = createResource(
    () => {
      const organization = organizationSlug()
      if (!organization) return null
      return {
        ...engagementPeriod(),
        page: page(),
        sortBy: sortBy(),
        sortOrder: sortOrder(),
        organization
      }
    },
    async (params) => {
      if (!params) return null

      const dateRanges =
        params.selectedPeriod === 'custom' && params.customDateRange
          ? getDateRangesForCustomPeriod(params.customDateRange.from, params.customDateRange.to)
          : getDateRangesForSimplePeriod(params.selectedPeriod)

      let fromPrevious = dateRanges.fromPrevious
      let toPrevious = dateRanges.toPrevious

      if (params.compareMode === 'custom_range' && params.compareCustomRange) {
        fromPrevious = formatDateToApiString(params.compareCustomRange.from)
        toPrevious = formatDateToApiString(params.compareCustomRange.to)
      }

      return await getChat.execute({
        fromDate: dateRanges.fromDate,
        toDate: dateRanges.toDate,
        fromPrevious,
        toPrevious,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
        organization: params.organization
      })
    }
  )

  const isEmpty = createMemo(() => {
    const data = chatData()
    return (
      data &&
      data.channels.length === 0 &&
      data.summary.activeUsers.current === 0 &&
      data.summary.messages.current === 0 &&
      data.summary.uniqueChatters.current === 0
    )
  })

  const handleSort = (column: string, direction: 'asc' | 'desc') => {
    setSortBy(column)
    setSortOrder(direction)
    setPage(1)
  }

  const handlePeriodChange = (period: SimplePeriodType) => {
    setEngagementPeriod({ selectedPeriod: period })
    setPage(1)
  }

  const handleCustomRangeChange = (from: Date, to: Date) => {
    setEngagementPeriod({ customDateRange: { from, to } })
    setPage(1)
  }

  const handleCompareModeChange = (mode: CompareMode) => {
    setEngagementPeriod({ compareMode: mode })
  }

  const handleCompareCustomRangeChange = (from: Date, to: Date) => {
    setEngagementPeriod({ compareCustomRange: { from, to } })
  }

  return (
    <div class="flex flex-col gap-4 px-6">
      <div
        use:sticky={setIsStuck}
        class="sticky top-0 z-10 py-6 transition-colors duration-200 -mx-6 px-6"
        classList={{
          'bg-white border-b border-gray-200': isStuck(),
          'bg-transparent border-b border-transparent': !isStuck()
        }}
      >
        <div class="mx-auto flex w-full max-w-[1280px] flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <PeriodSelector
            selectedPeriod={engagementPeriod().selectedPeriod}
            onPeriodChange={handlePeriodChange}
            customDateRange={engagementPeriod().customDateRange}
            onCustomRangeChange={handleCustomRangeChange}
            compareMode={engagementPeriod().compareMode}
            onCompareModeChange={handleCompareModeChange}
            compareCustomRange={engagementPeriod().compareCustomRange}
            onCompareCustomRangeChange={handleCompareCustomRangeChange}
          />
        </div>
      </div>
      <div class="mx-auto flex w-full max-w-[1280px] flex-col gap-8">
        <Show when={!chatData.loading && chatData() && isEmpty()}>
          <EmptyState />
        </Show>

        <Show when={!isEmpty()}>
          <Show
            when={chatData()?.summary}
            fallback={
              <div class="grid grid-cols-2 gap-4 sm:grid-cols-2">
                <SkeletonSummaryCard />
                <SkeletonSummaryCard />
              </div>
            }
          >
            <div class="grid grid-cols-2 gap-4 sm:grid-cols-2">
              <SummaryCard
                title="ls_chat_total_messages"
                value={chatData()!.summary.messages.current}
                change={chatData()!.summary.messages.diff}
              />
              <SummaryCard
                title="ls_chat_unique_messages"
                value={chatData()!.summary.uniqueChatters.current}
                change={chatData()!.summary.uniqueChatters.diff}
              />
            </div>
          </Show>

          <Show when={chatData()?.timeSeries} fallback={<SkeletonChart legendItems={3} />}>
            <ChatChart
              data={chatData()!.timeSeries}
              showActiveUsers={false}
              showTotalMessages={true}
              showUniqueChatters={true}
            />
          </Show>

          <ChatRoomsTable
            channels={chatData()?.channels ?? []}
            totalChannels={chatData()?.channels?.length ?? 0}
            page={page}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
            onSort={handleSort}
            sortColumn={sortBy}
            sortDirection={sortOrder}
            isLoading={chatData.state === 'unresolved' || chatData.loading}
          />
        </Show>
      </div>
    </div>
  )
}

export default Chat
